import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { UserListItem, UserProfile } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { UserAvatar } from "../../components/ui/Avatar";
import { UserBadges } from "../../components/UserBadges";
import { Button, IconButton } from "../../components/ui/Button";
import { toast } from "../../components/ui/Toast";
import { IconUserCheck, IconUserPlus } from "../../components/icons";
import { useAuth, useAuthGate } from "../auth/AuthProvider";

/** Optimistic follow state shared by the text and icon follow controls. */
function useFollow(userId: string, following: boolean, onChange?: (v: boolean, followerCount: number) => void) {
  const { me } = useAuth();
  const gate = useAuthGate();
  const qc = useQueryClient();
  const [state, setState] = useState(following);
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (!pending) setState(following);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [following]);

  const toggle = async () => {
    const next = !state;
    setState(next);
    setPending(true);
    try {
      const r = await api.put<{ following: boolean; followerCount: number }>(`/api/users/${userId}/follow`, { value: next });
      onChange?.(next, r.followerCount);
      qc.setQueriesData<UserProfile>({ queryKey: ["profile"] }, (old) => (old && old.id === userId ? { ...old, isFollowing: next, followerCount: r.followerCount } : old));
      void qc.invalidateQueries({ queryKey: ["feed", "following"] });
      void qc.invalidateQueries({ queryKey: ["profile", me?.username] });
    } catch (e) {
      setState(!next);
      toast.error(errorMessage(e, "Couldn't update follow"));
    } finally {
      setPending(false);
    }
  };
  return { self: me?.id === userId, following: state, toggle: gate(() => void toggle()) };
}

export function FollowButton({ userId, following, size = "md", onChange }: { userId: string; following: boolean; size?: "sm" | "md"; onChange?: (v: boolean, followerCount: number) => void }) {
  const f = useFollow(userId, following, onChange);
  const [hover, setHover] = useState(false);
  if (f.self) return null;
  const current = f.following;
  return (
    <Button
      size={size === "sm" ? "sm" : "md"}
      variant={current ? "outline" : "primary"}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        f.toggle();
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={cn("follow-btn min-w-[84px]", current && hover && "border-danger/50 text-danger hover:bg-danger-soft")}
      aria-pressed={current}
    >
      {current ? (hover ? "Unfollow" : "Following") : "Follow"}
    </Button>
  );
}

/** Icon-only follow toggle for dense lists such as the discovery panel. */
export function FollowIconButton({ user }: { user: Pick<UserListItem, "id" | "displayName" | "isFollowing"> }) {
  const f = useFollow(user.id, user.isFollowing);
  if (f.self) return null;
  return (
    <IconButton
      size="sm"
      label={f.following ? `Unfollow ${user.displayName}` : `Follow ${user.displayName}`}
      icon={f.following ? IconUserCheck : IconUserPlus}
      aria-pressed={f.following}
      className={cn(f.following && "text-accent hover:text-accent")}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        f.toggle();
      }}
    />
  );
}

export function UserRow({ user, compact, panel, action, followsYou }: { user: UserListItem; compact?: boolean; panel?: boolean; action?: React.ReactNode; followsYou?: boolean }) {
  if (panel)
    return (
      <div className="flex h-11 items-center gap-1 rounded-lg pl-2 pr-1 transition-colors hover:bg-surface">
        <Link to={`/u/${user.username}`} className="flex min-w-0 flex-1 items-center gap-3">
          <UserAvatar user={user} size="sm" plain />
          <span className="min-w-0 truncate text-sm text-fg-2">{user.displayName}</span>
          <UserBadges user={user} max={1} />
          <span className="min-w-0 flex-1 truncate text-xs text-fg-3">@{user.username}</span>
        </Link>
        {action ?? (user.bot ? null : <FollowIconButton user={user} />)}
      </div>
    );
  return (
    <div className={cn("flex items-center gap-3 px-4 transition-colors hover:bg-surface/60", compact ? "py-2" : "py-3")}>
      <Link to={`/u/${user.username}`} className="flex min-w-0 flex-1 items-center gap-3">
        <UserAvatar user={user} size={compact ? "md" : "lg"} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 truncate text-sm font-semibold text-fg">
            {user.displayName}
            <UserBadges user={user} />
          </div>
          <div className="flex items-center gap-1.5 truncate text-xs text-fg-3">
            @{user.username}
            {followsYou && <span className="rounded bg-surface-2 px-1 py-px font-medium text-fg-2">Follows you</span>}
          </div>
          {!compact && user.bio && <p className="mt-1 line-clamp-2 text-sm text-fg-2">{user.bio}</p>}
        </div>
      </Link>
      {action ?? (user.bot ? null : <FollowButton userId={user.id} following={user.isFollowing} size="sm" />)}
    </div>
  );
}
